import type {
  OrgEventAutomationCondition,
  OrgEventAutomationSource,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  newConditionId,
  sourceForPreset,
} from 'components/MapPage/CorporateViewTabs/EventsTab/automationRuleLogic';
import type { RuleDraftPayload } from 'components/MapPage/CorporateViewTabs/EventsTab/ruleFormModel';
import {
  WEATHER_FEATURESERVER_CATALOG,
  catalogSourceDefaults,
  findWeatherLayerById,
} from 'components/MapPage/CorporateViewTabs/EventsTab/weatherFeatureServerCatalog';

export type RuleTemplateId =
  | 'hurricane-cone-aor'
  | 'wildfire-near-assets'
  | 'nws-extreme-aor'
  | 'earthquake-near-aor';

export type RuleTemplate = {
  id: RuleTemplateId;
  title: string;
  description: string;
  /** Needs at least one AOR to be useful. */
  needsAors: boolean;
  /** Needs imported assets to be useful. */
  needsAssets: boolean;
  build: (aorIds: string[]) => RuleDraftPayload;
};

const WILDFIRE_RADIUS_KM = 16;
const EARTHQUAKE_RADIUS_KM = 100;
const EARTHQUAKE_MIN_MAGNITUDE = '5';

/** A rule source for a weather catalog layer, with its id/occurrence defaults. */
export function catalogFeedSource(catalogId: string): OrgEventAutomationSource {
  const layer = findWeatherLayerById(catalogId);
  if (!layer) throw new Error(`Unknown catalog layer ${catalogId}`);
  const defaults = catalogSourceDefaults(layer.serviceUrl);
  return {
    type: 'arcgis_feature',
    preset: 'custom',
    serviceUrl: layer.serviceUrl,
    layerLabel: layer.name,
    ...(defaults.featureIdField
      ? { featureIdField: defaults.featureIdField }
      : {}),
    ...(defaults.occurrenceBy ? { occurrenceBy: defaults.occurrenceBy } : {}),
  };
}

function aorIntersect(
  source: OrgEventAutomationSource,
  aorIds: string[],
  geometryFrom: 'cone' | 'feature',
  proximityKm = 0
): OrgEventAutomationCondition {
  return {
    id: newConditionId(),
    kind: 'aor_intersect',
    aorIds,
    minCount: 1,
    geometryFrom,
    proximityKm,
    source,
  };
}

function draft(params: {
  name: string;
  source: OrgEventAutomationSource;
  conditions: OrgEventAutomationCondition[];
  severity: string;
  type: string;
  fromImpactedAors: boolean;
}): RuleDraftPayload {
  return {
    name: params.name,
    enabled: false,
    logic: 'all',
    source: params.source,
    conditions: params.conditions,
    event_template: {
      severity: params.severity,
      type: params.type,
      status: 'Active',
      aorAssignment: {
        strategy: params.fromImpactedAors
          ? 'from_impacted_aors'
          : 'from_resources',
      },
    },
    notify_on_create: false,
  };
}

/**
 * Starter rules for organization setup, built on the analytics-ready feeds and
 * scoped to the org's AORs. Every template saves disabled.
 */
export const RULE_TEMPLATES: readonly RuleTemplate[] = [
  {
    id: 'hurricane-cone-aor',
    title: 'Hurricane cone reaches an AOR',
    description:
      'Opens an event when an NHC forecast cone overlaps any of your AORs.',
    needsAors: true,
    needsAssets: false,
    build: (aorIds) => {
      const source = sourceForPreset('tropical');
      return draft({
        name: 'Hurricane cone reaches our AORs',
        source,
        conditions: [aorIntersect(source, aorIds, 'cone')],
        severity: 'High',
        type: 'Tropical Storm',
        fromImpactedAors: true,
      });
    },
  },
  {
    id: 'wildfire-near-assets',
    title: 'Wildfire within 10 miles of an asset',
    description:
      'Opens an event when an active wildfire incident is within 10 miles of an imported asset.',
    needsAors: false,
    needsAssets: true,
    build: () => {
      const source = sourceForPreset('wildfire_incidents');
      return draft({
        name: 'Wildfire near our assets',
        source,
        conditions: [
          {
            id: newConditionId(),
            kind: 'resource_intersect',
            minCount: 1,
            geometryFrom: 'feature',
            proximityKm: WILDFIRE_RADIUS_KM,
            source,
          },
        ],
        severity: 'High',
        type: 'Wildfire',
        fromImpactedAors: false,
      });
    },
  },
  {
    id: 'nws-extreme-aor',
    title: 'NWS extreme warning in an AOR',
    description:
      'Opens an event when an extreme-severity NWS watch or warning overlaps an AOR.',
    needsAors: true,
    needsAssets: false,
    build: (aorIds) => {
      const source = catalogFeedSource('nws-extreme-events');
      return draft({
        name: 'NWS extreme warning in our AORs',
        source,
        conditions: [aorIntersect(source, aorIds, 'feature')],
        severity: 'High',
        type: 'Severe Weather',
        fromImpactedAors: true,
      });
    },
  },
  {
    id: 'earthquake-near-aor',
    title: 'Magnitude 5+ earthquake near an AOR',
    description:
      'Opens an event for a magnitude 5 or greater earthquake within 100 km of an AOR.',
    needsAors: true,
    needsAssets: false,
    build: (aorIds) => {
      const source = catalogFeedSource('usgs-earthquakes');
      return draft({
        name: 'Magnitude 5+ earthquake near our AORs',
        source,
        conditions: [
          {
            id: newConditionId(),
            kind: 'attribute',
            field: 'mag',
            operator: '>=',
            value: EARTHQUAKE_MIN_MAGNITUDE,
            source,
          },
          aorIntersect(source, aorIds, 'feature', EARTHQUAKE_RADIUS_KM),
        ],
        severity: 'High',
        type: 'Earthquake',
        fromImpactedAors: true,
      });
    },
  },
];

/** Event Settings search param that opens a new rule watching this feed id. */
export const NEW_RULE_FEED_PARAM = 'newRuleFeed';

/**
 * Feed id for the global Active Hurricanes web map, which maps to the tropical
 * preset.
 */
export const TROPICAL_FEED_ID = 'active-hurricanes';

const ACTIVE_HURRICANES_ITEM_ID = '248e7b5827a34b248647afb012c58787';
const ACTIVE_HURRICANES_NAME = 'active hurricanes / tropical cyclones (global)';

/**
 * Rule feed for an Analytics section layer ref (`{id, name}`), or null when the
 * layer has no matching feed. Seeded layers carry an `external_layers` UUID, so
 * the catalog name is the fallback.
 */
export function ruleFeedIdForLayer(layer: {
  id?: string;
  name?: string;
}): string | null {
  const name = layer.name?.trim().toLowerCase() ?? '';
  if (
    layer.id === ACTIVE_HURRICANES_ITEM_ID ||
    name === ACTIVE_HURRICANES_NAME
  ) {
    return TROPICAL_FEED_ID;
  }
  const byId = findWeatherLayerById(layer.id);
  if (byId) return byId.id;
  return (
    WEATHER_FEATURESERVER_CATALOG.find(
      (entry) => entry.name.toLowerCase() === name
    )?.id ?? null
  );
}

/** A blank-ish draft watching one feed, for "Create a rule from this layer". */
export function draftForCatalogFeed(
  catalogId: string
): RuleDraftPayload | null {
  if (catalogId === TROPICAL_FEED_ID) {
    const source = sourceForPreset('tropical');
    return {
      name: 'Hurricane cone alert',
      enabled: false,
      logic: 'all',
      source,
      conditions: [
        {
          id: newConditionId(),
          kind: 'resource_intersect',
          minCount: 1,
          geometryFrom: 'cone',
          source,
        },
      ],
      notify_on_create: false,
    };
  }
  const layer = findWeatherLayerById(catalogId);
  if (!layer) return null;
  const source = catalogFeedSource(catalogId);
  return {
    name: `${layer.name} alert`,
    enabled: false,
    logic: 'all',
    source,
    conditions: [
      {
        id: newConditionId(),
        kind: 'resource_intersect',
        minCount: 1,
        geometryFrom: 'feature',
        source,
      },
    ],
    notify_on_create: false,
  };
}
